/**
 * Aspect — Cinematic sample-tour reel
 * --------------------------------------------------------------
 * Scripted camera + live engine features for the launch film (Act 2).
 * Boot with `?reel=1`. Record 1080p from the canvas (OBS / Windows Game Bar).
 *
 * Sequence: dollhouse orbit → drop into walk → product cards / discounts →
 * live swap → kitchen glide (doors open) → noon→night→day.
 */

import * as pc from "playcanvas";
import { DEFAULT_INITIAL_HOUR } from "../scene/day_night_cycle.js";

const EYE = 1.6;
const EASE = (t) => {
  const x = Math.max(0, Math.min(1, t));
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
};

function wait(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function waitUntil(pred, timeoutMs = 12000) {
  return new Promise((resolve, reject) => {
    const t0 = performance.now();
    const tick = () => {
      if (pred()) return resolve();
      if (performance.now() - t0 > timeoutMs) return reject(new Error("reel wait timeout"));
      requestAnimationFrame(tick);
    };
    tick();
  });
}

export class CinematicReel {
  /**
   * @param {object} opts
   * @param {pc.AppBase} opts.app
   * @param {pc.Entity} opts.camera
   * @param {import('../scene_manager.js').SceneManager} opts.scene
   * @param {import('./first_person_controls.js').FirstPersonControls} opts.fp
   * @param {import('./scene_view_controls.js').SceneViewControls} opts.sceneView
   * @param {import('./tour_camera.js').TourCamera} opts.tourCam
   * @param {import('../scene/day_night_cycle.js').DayNightCycle} opts.dayNight
   * @param {import('../ui/overlay.js').Overlay} [opts.overlay]
   */
  constructor({ app, camera, scene, fp, sceneView, tourCam, dayNight, overlay = null }) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.fp = fp;
    this.sceneView = sceneView;
    this.tourCam = tourCam;
    this.dayNight = dayNight;
    this.overlay = overlay;
    this._running = false;
    this._hover = null;
    this._unsub = null;
  }

  get running() {
    return this._running;
  }

  async start() {
    if (this._running) return;
    this._running = true;
    document.body.classList.add("cinematic-reel");
    if (this.overlay) this.overlay._hoverUiOn = true;
    this.fp?.setEnabled?.(false);
    this.fp?.releaseLock?.();
    this.tourCam?.stop?.();

    this._unsub = () => this.app.off("update", this._onUpdate);
    this._onUpdate = (dt) => this._tick(dt);
    this.app.on("update", this._onUpdate);

    try {
      await waitUntil(() => this.scene.room && this.scene.placements.size >= 2, 20000);
      await wait(600);
      await this._actOverhead();
      await this._actWalkAndPrices();
      await this._actSwap();
      await this._actKitchen();
      await this._actDayNight();
      this._hideHover();
      console.info("[reel] complete — hold this frame for the lockup");
    } catch (err) {
      console.warn("[reel] stopped", err);
    }
  }

  stop() {
    if (!this._running) return;
    this._running = false;
    this._hideHover();
    this._unsub?.();
    document.body.classList.remove("cinematic-reel");
    this.fp?.setEnabled?.(true);
  }

  _tick() {
    if (!this._running || !this._hover) return;
    this._publishHover(this._hover.placement);
  }

  async _actOverhead() {
    if (!this.sceneView.overheadActive) this.sceneView.enable();
    await waitUntil(() => this.sceneView.enabled && !this.sceneView.isTransitioning, 4000);
    await this._animate(8.5, (k, dk) => {
      this.sceneView.orbitBy(52 * dk);
    });
  }

  async _actWalkAndPrices() {
    this.sceneView.disable();
    await waitUntil(() => !this.sceneView.overheadActive, 4000);
    this.fp?.setEnabled?.(false);

    const sofa = this._findPlacement(/sofa|couch/i, "living");
    const table = this._findPlacement(/coffee|table/i, "living") || this._findPlacement(/table/i);
    const living = this._roomByName(/living/i);
    const start = this.camera.getPosition().clone();
    const floorY = this.scene.room?.floorY ?? 0;
    const look = sofa ? this._placementPos(sofa) : living ? new pc.Vec3(living.cx, floorY + EYE, living.cz) : start;
    const eye = new pc.Vec3(look.x, floorY + EYE, look.z);
    // Stand a few metres back, facing the piece.
    const back = new pc.Vec3(eye.x + 2.4, floorY + EYE, eye.z + 2.1);

    await this._dolly(start, back, 3.2, look);
    if (sofa) {
      this._hover = { placement: sofa };
      await wait(3.6);
    }
    if (table && table !== sofa) {
      await this._lookAt(this._placementPos(table), 1.4);
      this._hover = { placement: table };
      await wait(2.8);
    }
  }

  async _actSwap() {
    const sofa = this._findPlacement(/sofa|couch/i, "living");
    if (!sofa || this.scene.readOnly) return;
    const alt = this._altAsset(sofa);
    if (!alt) return;
    this._hover = { placement: sofa };
    await this.scene.swap(sofa.id, alt);
    await wait(2.8);
  }

  async _actKitchen() {
    this._hideHover();
    const kitchen = this._roomByName(/kitchen/i);
    if (!kitchen) return;
    const rooms = this.tourCam?.rooms?.() ?? [];
    const hit = rooms.find((r) => /kitchen/i.test(r.name ?? ""));
    if (hit) {
      this.tourCam.goToRoom(hit.index);
      await waitUntil(() => !this.tourCam._glide, 5000);
      await wait(1.6);
      return;
    }
    const floorY = this.scene.room?.floorY ?? 0;
    await this._dolly(
      this.camera.getPosition().clone(),
      new pc.Vec3(kitchen.cx, floorY + EYE, kitchen.cz),
      3.4,
      new pc.Vec3(kitchen.cx, floorY + EYE, kitchen.cz),
    );
  }

  async _actDayNight() {
    const noon = DEFAULT_INITIAL_HOUR;
    await this._setHour(21.2, 7.2);
    await wait(2.2);
    await this._setHour(noon, 4.4);
    await wait(1.2);
  }

  _setHour(target, duration) {
    const from = this.dayNight?.hour ?? DEFAULT_INITIAL_HOUR;
    return this._animate(duration, (k) => {
      let h = from + (target - from) * k;
      h = ((h % 24) + 24) % 24;
      this.dayNight?.setHour(h, true);
    });
  }

  _dolly(from, to, duration, lookAt) {
    return this._animate(duration, (k) => {
      const x = from.x + (to.x - from.x) * k;
      const y = from.y + (to.y - from.y) * k;
      const z = from.z + (to.z - from.z) * k;
      this.camera.setLocalPosition(x, y, z);
      if (lookAt) this._aim(lookAt);
      this.fp?.adoptPose?.(this.camera.getPosition(), this._yaw, this._pitch);
      this.app.renderNextFrame = true;
    });
  }

  _lookAt(target, duration) {
    return this._animate(duration, () => {
      this._aim(target);
      this.app.renderNextFrame = true;
    });
  }

  _aim(target) {
    const pos = this.camera.getPosition();
    const dx = target.x - pos.x;
    const dy = target.y - pos.y;
    const dz = target.z - pos.z;
    const horiz = Math.hypot(dx, dz) || 1e-6;
    this._yaw = (Math.atan2(-dx, -dz) * 180) / Math.PI;
    this._pitch = pc.math.clamp((Math.atan2(dy, horiz) * 180) / Math.PI, -40, 25);
    this.camera.setLocalEulerAngles(this._pitch, this._yaw, 0);
  }

  _animate(duration, fn) {
    return new Promise((resolve) => {
      let t = 0;
      const step = (dt) => {
        if (!this._running) {
          this.app.off("update", step);
          return resolve();
        }
        t = Math.min(1, t + (dt ?? 0.016) / duration);
        const k = EASE(t);
        const prev = this._animK ?? 0;
        fn(k, k - prev);
        this._animK = k;
        if (t >= 1) {
          this._animK = 0;
          this.app.off("update", step);
          resolve();
        }
      };
      this.app.on("update", step);
    });
  }

  _findPlacement(nameRe, category = null) {
    const list = [...this.scene.placements.values()];
    const scored = [];
    for (const p of list) {
      const meta = this.scene.catalog?.get?.(p.asset_id);
      const name = String(meta?.name ?? p.asset_id ?? "");
      if (!nameRe.test(name) && !nameRe.test(String(p.asset_id ?? ""))) continue;
      const fp = meta?.footprint ?? p.footprint;
      const area = (Number(fp?.w) || 0) * (Number(fp?.d) || 0);
      scored.push({ p, area });
    }
    scored.sort((a, b) => b.area - a.area);
    if (scored[0]) return scored[0].p;
    if (!category) return null;
    return list.find((p) => this.scene.catalog?.get?.(p.asset_id)?.category === category) ?? null;
  }

  _altAsset(placement) {
    const meta = this.scene.catalog?.get?.(placement.asset_id);
    const cat = meta?.category;
    if (!cat || !this.scene.catalog) return null;
    let best = null;
    for (const a of this.scene.catalog.values()) {
      const id = a.asset_id;
      if (!id || id === placement.asset_id) continue;
      if (a.category !== cat) continue;
      const area = (Number(a.footprint?.w) || 0) * (Number(a.footprint?.d) || 0);
      if (area < 0.35) continue;
      const sofaLike = /sofa|couch/i.test(String(a.name ?? ""));
      const score = area + (sofaLike ? 10 : 0);
      if (!best || score > best.score) best = { id, score };
    }
    return best?.id ?? null;
  }

  _placementPos(p) {
    const floorY = this.scene.room?.floorY ?? 0;
    const [x, y, z] = p.position ?? [0, floorY, 0];
    return new pc.Vec3(Number(x) || 0, (Number(y) || floorY) + 0.55, Number(z) || 0);
  }

  _roomByName(re) {
    const rooms = this.tourCam?.rooms?.() ?? [];
    return rooms.find((r) => re.test(r.name ?? r.type ?? "")) ?? null;
  }

  _publishHover(p) {
    if (!p) return;
    const meta = this.scene.catalog?.get?.(p.asset_id) ?? {};
    const fp = meta.footprint ?? p.footprint;
    const cm = (v) => Math.round(Number(v) * 100);
    const entity = this.scene.entities?.get?.(p.id);
    const pos = entity ? entity.getPosition() : this._placementPos(p);
    const sv = this.camera.camera.worldToScreen(pos, this._screen ?? (this._screen = new pc.Vec3()));
    const canvas = this.app.graphicsDevice?.canvas;
    const rect = canvas?.getBoundingClientRect?.();
    const screen =
      sv.z > 0 && rect
        ? { x: sv.x + rect.left, y: sv.y + rect.top }
        : { x: window.innerWidth * 0.62, y: window.innerHeight * 0.42 };
    this.app.fire("scene:hover-info", {
      info: {
        id: p.id,
        name: meta.name ?? p.asset_id ?? "Item",
        brand: meta.brand ?? null,
        category: meta.category ?? "furniture",
        material: meta.material ?? null,
        price: p.price ?? meta.price ?? null,
        dims: fp ? `${cm(fp.w)}cm × ${cm(fp.d)}cm` : null,
      },
      screen,
    });
  }

  _hideHover() {
    this._hover = null;
    this.app.fire("scene:hover-info", { info: null });
  }
}

export default CinematicReel;
